import { useState, useMemo } from "react"
import {
  TrendingUp, Search, ExternalLink, UserPlus, Loader2,
  BarChart3, Eye, PenTool, CheckCircle2, Send as SendIcon,
  Filter, Download, Sparkles, X, Settings, Copy, Globe,
} from "lucide-react"
import * as Dialog from "@radix-ui/react-dialog"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/Badge"
import { Button } from "@/components/ui/Button"
import { DataGrid, type DataGridColumn } from "@/components/ui/DataGrid"
import {
  useMarketOpportunities,
  useMarketStats,
  useUpdateMarketStatus,
  useCreateLeadFromOpportunity,
  useTriggerMarketSearch,
  useTranslateOpportunity,
  type MarketOpportunity,
} from "@/hooks/useMarketAnalysis"
import { useAuthStore } from "@/stores/authStore"
import { useNavigate } from "react-router-dom"

// ─── Constants ───

const PRODUCTS = [
  { value: "", label: "Todos os Produtos" },
  { value: "IGA", label: "IGA" },
  { value: "Data Privacy", label: "Data Privacy" },
  { value: "GRCWay", label: "GRCWay" },
  { value: "ESG", label: "ESG" },
  { value: "Canal de Denuncias", label: "Canal de Denuncias" },
  { value: "Risk Management", label: "Risk Management" },
  { value: "Data Discovery", label: "Data Discovery" },
]

const SOURCES = [
  { value: "", label: "Todas as Origens" },
  { value: "Blog", label: "Blog" },
  { value: "LinkedIn", label: "LinkedIn" },
  { value: "News", label: "News" },
  { value: "Forum", label: "Forum" },
  { value: "Regulatorio", label: "Regulatorio" },
]

const STATUSES = [
  { value: "", label: "Todos os Status" },
  { value: "novo", label: "Novo" },
  { value: "criacao", label: "Criacao" },
  { value: "aprovacao", label: "Aprovacao" },
  { value: "publicado", label: "Publicado" },
]

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  novo:      { label: "Novo",      color: "text-gray-400",   bg: "bg-gray-500/10 border-gray-500/30",   icon: Eye },
  criacao:   { label: "Criacao",   color: "text-blue-400",   bg: "bg-blue-500/10 border-blue-500/30",   icon: PenTool },
  aprovacao: { label: "Aprovacao", color: "text-amber-400",  bg: "bg-amber-500/10 border-amber-500/30", icon: CheckCircle2 },
  publicado: { label: "Publicado", color: "text-green-400",  bg: "bg-green-500/10 border-green-500/30", icon: SendIcon },
}

const SOURCE_COLOR: Record<string, string> = {
  Blog: "#3b82f6",
  LinkedIn: "#0a66c2",
  News: "#f59e0b",
  Forum: "#6b7280",
  Regulatorio: "#a855f7",
}

const PRODUCT_COLOR: Record<string, string> = {
  IGA: "#8b5cf6",
  "Data Privacy": "#10b981",
  GRCWay: "#3b82f6",
  ESG: "#84cc16",
  "Canal de Denuncias": "#ef4444",
  "Risk Management": "#f97316",
  "Data Discovery": "#14b8a6",
}

// ─── Helpers ───

function formatDate(d: string) {
  if (!d) return "-"
  try {
    return new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
  } catch {
    return d
  }
}

function getYesterday() {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

function getToday() {
  return new Date().toISOString().slice(0, 10)
}

// ─── Sub-components ───

function StatCard({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: number | string; color: string }) {
  return (
    <div className="bg-card border rounded-xl p-4 flex flex-col items-center gap-1.5 min-w-[140px]">
      <Icon className="w-5 h-5" style={{ color }} />
      <span className="text-2xl font-bold tabular-nums">{value}</span>
      <span className="text-xs text-muted-foreground font-medium">{label}</span>
    </div>
  )
}

function StatusDropdown({ item, onStatusChange, isLoading }: {
  item: MarketOpportunity
  onStatusChange: (id: string, status: string, approvalLink?: string) => void
  isLoading: boolean
}) {
  const [open, setOpen] = useState(false)
  const [approvalInput, setApprovalInput] = useState("")
  const [showApprovalInput, setShowApprovalInput] = useState(false)
  const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.novo
  const StatusIcon = cfg.icon

  const statuses = ["novo", "criacao", "aprovacao", "publicado"]

  function handleSelect(s: string) {
    if (s === item.status) { setOpen(false); return }
    if (s === "aprovacao") {
      setShowApprovalInput(true)
      return
    }
    onStatusChange(item.id, s)
    setOpen(false)
  }

  function submitApproval() {
    onStatusChange(item.id, "aprovacao", approvalInput || undefined)
    setShowApprovalInput(false)
    setApprovalInput("")
    setOpen(false)
  }

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => { if (!isLoading) setOpen(!open) }}
        disabled={isLoading}
        className={cn(
          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition-colors",
          cfg.bg, cfg.color,
          "hover:opacity-80",
        )}
      >
        {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <StatusIcon className="w-3.5 h-3.5" />}
        {cfg.label}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setShowApprovalInput(false) }} />
          <div className="absolute right-0 mt-1 z-50 bg-popover border rounded-lg shadow-lg py-1 min-w-[180px]">
            {showApprovalInput ? (
              <div className="p-3 space-y-2">
                <label className="text-xs text-muted-foreground font-medium">Link de aprovacao (opcional)</label>
                <input
                  type="url"
                  value={approvalInput}
                  onChange={e => setApprovalInput(e.target.value)}
                  placeholder="https://..."
                  className="w-full rounded-md border bg-background px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button onClick={() => { setShowApprovalInput(false) }} className="flex-1 text-xs py-1 rounded border hover:bg-accent transition-colors">Cancelar</button>
                  <button onClick={submitApproval} className="flex-1 text-xs py-1 rounded bg-amber-500 text-white hover:bg-amber-600 transition-colors">Confirmar</button>
                </div>
              </div>
            ) : (
              statuses.map(s => {
                const c = STATUS_CONFIG[s]
                const Ic = c.icon
                return (
                  <button
                    key={s}
                    onClick={() => handleSelect(s)}
                    className={cn(
                      "flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors hover:bg-accent",
                      s === item.status && "bg-accent font-semibold",
                    )}
                  >
                    <Ic className={cn("w-3.5 h-3.5", c.color)} />
                    <span>{c.label}</span>
                    {s === item.status && <CheckCircle2 className="w-3 h-3 ml-auto text-primary" />}
                  </button>
                )
              })
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Search Modal ───

function SearchModal({ open, onClose, onSubmit, isLoading }: {
  open: boolean
  onClose: () => void
  onSubmit: (dateFrom: string, dateTo: string) => void
  isLoading: boolean
}) {
  const [dateFrom, setDateFrom] = useState(getYesterday())
  const [dateTo, setDateTo] = useState(getToday())

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card border rounded-2xl shadow-xl w-full max-w-md p-6 mx-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Buscar Novas Oportunidades</h3>
              <p className="text-xs text-muted-foreground">Analise de mercado com IA</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Data Inicio</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Data Fim</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
        </div>
        <Button
          onClick={() => onSubmit(dateFrom, dateTo)}
          disabled={isLoading}
          className="w-full"
          isLoading={isLoading}
        >
          {isLoading ? "Buscando oportunidades com IA..." : "Iniciar Busca"}
        </Button>
      </div>
    </div>
  )
}

// ─── DataGrid Columns ───

function buildColumns(
  handleStatusChange: (id: string, status: string, approvalLink?: string) => void,
  handleCreateLead: (id: string) => void,
  updateStatusPending: boolean,
  createLeadPending: boolean,
  onOpenDetail: (item: MarketOpportunity) => void,
): DataGridColumn<MarketOpportunity>[] {
  return [
    {
      id: "score",
      header: "Score",
      minWidth: "80px",
      sortable: true,
      filterable: false,
      accessor: (row) => {
        const color = row.score >= 9
          ? "text-green-400 bg-green-500/10 border-green-500/30"
          : row.score >= 7
            ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
            : "text-gray-400 bg-gray-500/10 border-gray-500/30"
        return (
          <span className={cn("inline-flex items-center justify-center w-10 h-7 rounded-md border text-xs font-bold tabular-nums", color)}>
            {row.score?.toFixed(1)}
          </span>
        )
      },
      sortValue: (row) => row.score ?? 0,
    },
    {
      id: "created_at",
      header: "Inclusao",
      minWidth: "140px",
      sortable: true,
      filterable: false,
      accessor: (row) => {
        if (!row.created_at) return <span className="text-xs text-muted-foreground">-</span>
        const d = new Date(row.created_at)
        return (
          <span className="text-xs text-muted-foreground tabular-nums">
            {d.toLocaleDateString("pt-BR")} {d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
          </span>
        )
      },
      sortValue: (row) => row.created_at ?? "",
    },
    {
      id: "title",
      header: "Titulo",
      sortable: true,
      filterable: true,
      minWidth: "250px",
      accessor: (row) => (
        <div className="min-w-0">
          <button
            onClick={(e) => { e.stopPropagation(); onOpenDetail(row); }}
            className="text-left font-medium truncate max-w-[300px] text-foreground hover:text-primary hover:underline cursor-pointer bg-transparent border-0 p-0 block"
          >
            {row.title}
          </button>
          {row.source_url && (
            <a
              href={row.source_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5"
            >
              Abrir fonte <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      ),
      sortValue: (row) => row.title ?? "",
      filterValue: (row) => row.title ?? "",
    },
    {
      id: "source",
      header: "Origem",
      minWidth: "100px",
      sortable: true,
      filterable: true,
      accessor: (row) => {
        const c = SOURCE_COLOR[row.source] || "#6b7280"
        return (
          <span
            className="inline-flex px-2 py-0.5 rounded-md border text-[11px] font-semibold"
            style={{ backgroundColor: `${c}20`, color: c, borderColor: `${c}30` }}
          >
            {row.source}
          </span>
        )
      },
      sortValue: (row) => row.source ?? "",
      filterValue: (row) => row.source ?? "",
    },
    {
      id: "published_date",
      header: "Data",
      minWidth: "100px",
      sortable: true,
      filterable: false,
      accessor: (row) => (
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatDate(row.published_date)}
        </span>
      ),
      sortValue: (row) => row.published_date ?? "",
    },
    {
      id: "product_tags",
      header: "Produtos",
      minWidth: "160px",
      filterable: true,
      accessor: (row) => (
        <div className="flex flex-wrap gap-1">
          {String(row.product_tags || "").split(",").filter(Boolean).map((tag, i) => {
            const c = PRODUCT_COLOR[tag.trim()] || "#6b7280"
            return (
              <span
                key={i}
                className="inline-flex px-1.5 py-0.5 rounded border text-[10px] font-semibold"
                style={{ backgroundColor: `${c}20`, color: c, borderColor: `${c}30` }}
              >
                {tag.trim()}
              </span>
            )
          })}
        </div>
      ),
      filterValue: (row) => row.product_tags ?? "",
    },
    {
      id: "summary",
      header: "Resumo",
      minWidth: "240px",
      filterable: true,
      accessor: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); onOpenDetail(row); }}
          className="text-left text-xs text-muted-foreground hover:text-foreground line-clamp-2 max-w-[220px] cursor-pointer bg-transparent border-0 p-0"
        >
          {row.summary}
        </button>
      ),
      filterValue: (row) => row.summary ?? "",
    },
    {
      id: "status",
      header: "Status",
      minWidth: "130px",
      sortable: true,
      filterable: true,
      accessor: (row) => (
        <StatusDropdown
          item={row}
          onStatusChange={handleStatusChange}
          isLoading={updateStatusPending}
        />
      ),
      sortValue: (row) => row.status ?? "",
      filterValue: (row) => STATUS_CONFIG[row.status]?.label ?? row.status ?? "",
    },
    {
      id: "worked_by_name",
      header: "Responsavel",
      minWidth: "140px",
      filterable: true,
      accessor: (row) =>
        row.worked_by_name ? (
          <div>
            <p className="text-xs font-medium">{row.worked_by_name}</p>
            <p className="text-[10px] text-muted-foreground">{formatDate(row.worked_at || "")}</p>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        ),
      filterValue: (row) => row.worked_by_name ?? "",
    },
    {
      id: "actions",
      header: "Acoes",
      minWidth: "140px",
      sortable: false,
      filterable: false,
      align: "right",
      accessor: (row) => (
        <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          {row.lead_id ? (
            <Badge variant="success" className="text-[10px] gap-1">
              <CheckCircle2 className="w-3 h-3" /> Lead criado
            </Badge>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1 text-green-400 border-green-500/30 hover:bg-green-500/10"
              onClick={() => handleCreateLead(row.id)}
              disabled={createLeadPending}
            >
              {createLeadPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
              Criar Lead
            </Button>
          )}
          {row.source_url && (
            <a
              href={row.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md border hover:bg-accent transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      ),
    },
  ]
}

// ─── Main Page ───

export default function MarketAnalysisPage() {
  // Detail modal state
  const [detailItem, setDetailItem] = useState<MarketOpportunity | null>(null)
  const [translations, setTranslations] = useState<{
    pt: { title: string; summary: string } | null
    en: { title: string; summary: string } | null
    es: { title: string; summary: string } | null
  } | null>(null)
  const [activeTab, setActiveTab] = useState<'original' | 'pt' | 'en' | 'es'>('original')
  const [copied, setCopied] = useState(false)
  const translateMutation = useTranslateOpportunity()

  // Filters
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [productFilter, setProductFilter] = useState("")
  const [sourceFilter, setSourceFilter] = useState("")
  const [scoreMin, setScoreMin] = useState(0)
  const [page, setPage] = useState(1)
  const perPage = 100

  // UI state
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const navigate = useNavigate()
  const currentUser = useAuthStore(s => s.user)
  const canSettings = (currentUser?.role?.hierarchy_level ?? 0) >= 70
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null)

  // Data
  const { data: opps, isLoading, isError } = useMarketOpportunities({
    status: statusFilter || undefined,
    product: productFilter || undefined,
    source: sourceFilter || undefined,
    score_min: scoreMin || undefined,
    search: search || undefined,
    page,
    per_page: perPage,
  })

  const { data: stats } = useMarketStats()

  const updateStatus = useUpdateMarketStatus()
  const createLead = useCreateLeadFromOpportunity()
  const triggerSearch = useTriggerMarketSearch()

  // Toast auto-dismiss
  useMemo(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000)
      return () => clearTimeout(t)
    }
  }, [toast])

  function openDetail(item: MarketOpportunity) {
    setDetailItem(item)
    setTranslations(null)
    setActiveTab('original')
    setCopied(false)
    translateMutation.mutate(item.id, {
      onSuccess: (data) => setTranslations(data),
    })
  }

  function copyContent() {
    if (!detailItem) return
    let text = ''
    if (activeTab === 'original') {
      text = `${detailItem.title}\n\n${detailItem.summary}`
    } else if (translations && translations[activeTab]) {
      const t = translations[activeTab]!
      text = `${t.title}\n\n${t.summary}`
    }
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function handleStatusChange(id: string, status: string, approvalLink?: string) {
    updateStatus.mutate({ id, status, approval_link: approvalLink }, {
      onSuccess: () => setToast({ msg: `Status alterado para "${STATUS_CONFIG[status]?.label}"`, type: "success" }),
      onError: () => setToast({ msg: "Erro ao alterar status", type: "error" }),
    })
  }

  function handleCreateLead(id: string) {
    createLead.mutate(id, {
      onSuccess: () => setToast({ msg: "Lead criado com sucesso!", type: "success" }),
      onError: () => setToast({ msg: "Erro ao criar lead", type: "error" }),
    })
  }

  function handleSearch(dateFrom: string, dateTo: string) {
    triggerSearch.mutate({ date_from: dateFrom, date_to: dateTo }, {
      onSuccess: () => {
        setSearchModalOpen(false)
        setToast({ msg: "Busca iniciada! Novas oportunidades serao exibidas em breve.", type: "success" })
      },
      onError: () => setToast({ msg: "Erro ao iniciar busca", type: "error" }),
    })
  }

  function handleExportCsv() {
    if (!opps?.items?.length) return
    const headers = ["Score", "Titulo", "Origem", "Data", "Produtos", "Status", "Responsavel"]
    const rows = opps.items.map(i => [
      i.score, i.title, i.source, formatDate(i.published_date),
      String(i.product_tags || ""), STATUS_CONFIG[i.status]?.label || i.status, i.worked_by_name || ""
    ])
    const csv = [headers.join(","), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = `analise-mercado-${getToday()}.csv`
    a.click()
  }

  const items = opps?.items || []

  const columns = useMemo(
    () => buildColumns(handleStatusChange, handleCreateLead, updateStatus.isPending, createLead.isPending, openDetail),
    [updateStatus.isPending, createLead.isPending],
  )

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={cn(
          "fixed top-4 right-4 z-[100] px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 animate-in slide-in-from-top-2",
          toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
        )}>
          {toast.type === "success" ? <CheckCircle2 className="w-4 h-4" /> : <X className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium text-primary uppercase tracking-wider mb-1">Marketing Outbound</p>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-primary" />
            Analise de Mercado
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Oportunidades identificadas por IA em blogs, noticias, LinkedIn e regulatorios</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {canSettings && (
            <Button variant="outline" size="sm" onClick={() => navigate('/settings?tab=registrations')}>
              <Settings className="w-4 h-4 mr-1.5" /> Configuracoes
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={!items.length}>
            <Download className="w-4 h-4 mr-1.5" /> Exportar CSV
          </Button>
          <Button size="sm" onClick={() => setSearchModalOpen(true)}>
            <Sparkles className="w-4 h-4 mr-1.5" /> Buscar Novas Oportunidades
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={BarChart3} label="Total" value={stats?.total ?? "-"} color="#a78bfa" />
        <StatCard icon={Eye} label="Novos" value={stats?.by_status?.novo ?? "-"} color="#9ca3af" />
        <StatCard icon={PenTool} label="Em Criacao" value={stats?.by_status?.criacao ?? "-"} color="#60a5fa" />
        <StatCard icon={SendIcon} label="Publicados" value={stats?.by_status?.publicado ?? "-"} color="#34d399" />
      </div>

      {/* Filters */}
      <div className="bg-card border rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Filtros</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Buscar por titulo..."
              className="w-full rounded-lg border bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <select
            value={productFilter}
            onChange={e => { setProductFilter(e.target.value); setPage(1) }}
            className="rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {PRODUCTS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <select
            value={sourceFilter}
            onChange={e => { setSourceFilter(e.target.value); setPage(1) }}
            className="rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
            className="rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground whitespace-nowrap">Score min:</label>
            <input
              type="range" min={0} max={10} step={0.5} value={scoreMin}
              onChange={e => { setScoreMin(Number(e.target.value)); setPage(1) }}
              className="flex-1 accent-primary"
            />
            <span className="text-xs font-mono font-bold w-6 text-right">{scoreMin}</span>
          </div>
        </div>
      </div>

      {/* DataGrid */}
      <div className="bg-card border rounded-xl overflow-hidden">
        {isError ? (
          <div className="text-center py-16">
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-destructive mb-1">Erro ao carregar dados</p>
              <p>A API de Analise de Mercado ainda nao esta disponivel. Os endpoints serao implementados em breve.</p>
            </div>
          </div>
        ) : (
          <DataGrid<MarketOpportunity>
            gridId="market-analysis"
            columns={columns}
            data={items}
            isLoading={isLoading}
            emptyMessage="Nenhuma oportunidade encontrada"
            emptyIcon={<TrendingUp className="w-8 h-8 text-muted-foreground/50" />}
            pageSize={20}
          />
        )}
      </div>

      {/* Detail Modal */}
      {detailItem && (
        <Dialog.Root open={!!detailItem} onOpenChange={(open) => { if (!open) setDetailItem(null) }}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in" />
            <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl max-h-[85vh] bg-card border rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4">

              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex px-2 py-0.5 rounded-md border text-[11px] font-semibold"
                    style={{
                      backgroundColor: `${SOURCE_COLOR[detailItem.source] || "#6b7280"}20`,
                      color: SOURCE_COLOR[detailItem.source] || "#6b7280",
                      borderColor: `${SOURCE_COLOR[detailItem.source] || "#6b7280"}30`,
                    }}
                  >
                    {detailItem.source}
                  </span>
                  <span className={cn(
                    "inline-flex items-center justify-center w-10 h-7 rounded-md border text-xs font-bold tabular-nums",
                    detailItem.score >= 9 ? "text-green-400 bg-green-500/10 border-green-500/30"
                      : detailItem.score >= 7 ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
                      : "text-gray-400 bg-gray-500/10 border-gray-500/30"
                  )}>
                    {detailItem.score?.toFixed(1)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {detailItem.published_date ? new Date(detailItem.published_date).toLocaleDateString('pt-BR') : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={copyContent} className="px-3 py-1.5 text-xs rounded-lg border hover:bg-accent transition-colors flex items-center gap-1.5">
                    {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copiado!' : 'Copiar'}
                  </button>
                  {detailItem.source_url && (
                    <a href={detailItem.source_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs rounded-lg border hover:bg-accent transition-colors flex items-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" /> Abrir Fonte
                    </a>
                  )}
                  <Dialog.Close className="p-1.5 rounded-lg hover:bg-accent transition-colors">
                    <X className="w-4 h-4" />
                  </Dialog.Close>
                </div>
              </div>

              {/* Language Tabs */}
              <div className="flex border-b">
                {([
                  { key: 'original', label: 'Original', flag: '' },
                  { key: 'pt', label: 'Portugues', flag: '\ud83c\udde7\ud83c\uddf7' },
                  { key: 'en', label: 'English', flag: '\ud83c\uddfa\ud83c\uddf8' },
                  { key: 'es', label: 'Espanol', flag: '\ud83c\uddea\ud83c\uddf8' },
                ] as const).map(tab => (
                  <button key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      "px-4 py-2.5 text-sm font-medium transition-colors relative flex items-center gap-1.5",
                      activeTab === tab.key
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    )}>
                    {tab.flag && <span>{tab.flag}</span>}
                    {tab.key === 'original' && <Globe className="w-3.5 h-3.5" />}
                    {tab.label}
                    {activeTab === tab.key && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />
                    )}
                  </button>
                ))}
                {translateMutation.isPending && (
                  <div className="flex items-center px-3 ml-auto">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    <span className="text-xs text-muted-foreground ml-2">Traduzindo...</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {activeTab === 'original' && (
                  <>
                    <h2 className="text-lg font-semibold mb-4 leading-tight">{detailItem.title}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{detailItem.summary}</p>
                  </>
                )}
                {activeTab !== 'original' && translations && translations[activeTab] && (
                  <>
                    <h2 className="text-lg font-semibold mb-4 leading-tight">{translations[activeTab]!.title}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{translations[activeTab]!.summary}</p>
                  </>
                )}
                {activeTab !== 'original' && !translations && !translateMutation.isPending && (
                  <p className="text-sm text-muted-foreground italic py-8 text-center">Traducao nao disponivel. Tente novamente.</p>
                )}
                {activeTab !== 'original' && translateMutation.isPending && (
                  <div className="flex flex-col items-center gap-3 py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Gerando traducao com IA...</span>
                  </div>
                )}

                {/* Product Tags */}
                {detailItem.product_tags && (
                  <div className="mt-6 pt-4 border-t flex flex-wrap gap-2">
                    {String(detailItem.product_tags).split(',').filter(Boolean).map((tag, i) => {
                      const c = PRODUCT_COLOR[tag.trim()] || "#6b7280"
                      return (
                        <span
                          key={i}
                          className="inline-flex px-2 py-0.5 rounded border text-[11px] font-semibold"
                          style={{ backgroundColor: `${c}20`, color: c, borderColor: `${c}30` }}
                        >
                          {tag.trim()}
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {/* Search Modal */}
      <SearchModal
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        onSubmit={handleSearch}
        isLoading={triggerSearch.isPending}
      />

    </div>
  )
}
